/**
 * Chatbot Service
 * VynDesk backend'in /api/chatbot/* proxy'si üzerinden ChatVyn (widget-api)
 * yönetimi. Müşteri ayrı login yapmaz — kimlik VynDesk JWT'sinden gelir.
 */
import api from '../lib/api';

export interface ChatbotLicense {
  id: string;
  domain: string;
  license_key?: string;
  metadata: Record<string, any>;
  created_at?: string;
  updated_at?: string;
}

export interface ChatbotLicensesResponse {
  success: boolean;
  data: {
    licenses: ChatbotLicense[];
    user: { maxLicenses: number; currentCount: number };
  };
}

export interface ChatbotConversation {
  id: string;
  title: string;
  domain: string;
  license_id: string;
  message_count: number;
  last_message_at?: string;
  created_at: string;
}

export interface ChatbotMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  created_at: string;
}

class ChatbotService {
  async getLicenses(): Promise<ChatbotLicensesResponse> {
    const res = await api.get<ChatbotLicensesResponse>('/chatbot/licenses');
    return res.data;
  }

  async createLicense(domain: string, metadata: Record<string, any>) {
    const res = await api.post('/chatbot/licenses', { domain, metadata });
    return res.data;
  }

  async deleteLicense(id: string) {
    const res = await api.delete(`/chatbot/licenses/${id}`);
    return res.data;
  }

  async getLicense(id: string) {
    const res = await api.get(`/chatbot/licenses/${id}`);
    return res.data;
  }

  async updateLicense(id: string, payload: { domain: string; metadata: Record<string, any> }) {
    const res = await api.patch(`/chatbot/licenses/${id}`, payload);
    return res.data;
  }

  async uploadAvatar(id: string, file: File) {
    const fd = new FormData();
    fd.append('file', file);
    const res = await api.post(`/chatbot/licenses/${id}/avatar`, fd);
    return res.data;
  }

  async deleteAvatar(id: string) {
    const res = await api.delete(`/chatbot/licenses/${id}/avatar`);
    return res.data;
  }

  async getProducts(id: string) {
    const res = await api.get(`/chatbot/licenses/${id}/products`);
    return res.data;
  }

  async uploadProducts(id: string, file: File) {
    const fd = new FormData();
    fd.append('file', file);
    const res = await api.post(`/chatbot/licenses/${id}/products`, fd);
    return res.data;
  }

  async deleteProducts(id: string) {
    const res = await api.delete(`/chatbot/licenses/${id}/products`);
    return res.data;
  }

  // ---- Shop-Verbindungen (Shopify / JTL) ----
  async getShopIntegrations(licenseId: string) {
    const res = await api.get(`/chatbot/licenses/${licenseId}/shop-integrations`);
    return res.data;
  }

  async createShopIntegration(licenseId: string, data: Record<string, any>) {
    const res = await api.post(`/chatbot/licenses/${licenseId}/shop-integrations`, data);
    return res.data;
  }

  async deleteShopIntegration(licenseId: string, integrationId: string) {
    const res = await api.delete(`/chatbot/licenses/${licenseId}/shop-integrations/${integrationId}`);
    return res.data;
  }

  async getConversations(licenseId: string) {
    const res = await api.get(`/chatbot/licenses/${licenseId}/conversations`);
    return res.data;
  }

  async getMessages(conversationId: string) {
    const res = await api.get(`/chatbot/conversations/${conversationId}/messages`);
    return res.data;
  }
}

export default new ChatbotService();
